import {json, redirect} from '@remix-run/node'
import {
  Form,
  useActionData,
  useFetcher,
  useLoaderData,
  useSearchParams,
} from '@remix-run/react'
import {withZod} from '@remix-validated-form/with-zod'
import {z} from 'zod'
import {isAxiosError} from 'axios'
import {zfd} from 'zod-form-data'
import {TextInput} from '~/components/Form/TextInput'
import {Button} from '~/components/Button'
import {requestPasswordReset, resetPassword} from '~/api/users'
import {commitSession, getUserSession} from '~/services/session.server'
import {logger} from '~/services/logger.server'
import {setSuccessMessage} from '~/services/message.server'
import type {ActionArgs, LoaderArgs} from '@remix-run/node'
import type {ApiResponseError} from '~/api/types'

export const ACTIONS = {
  START_FLOW: 'start-flow',
  RESTART_FLOW: 'restart-flow',
  UPDATE_PASSWORD: 'set-new-password',
} as const

const resetPasswordValidator = withZod(
  z
    .discriminatedUnion('action', [
      z.object({
        action: z.literal(ACTIONS.START_FLOW),
        email: zfd.text(z.string().email()),
      }),
      z.object({
        action: z.literal(ACTIONS.RESTART_FLOW),
      }),
      z.object({
        action: z.literal(ACTIONS.UPDATE_PASSWORD),
        password: zfd.text(
          z.string().min(12, 'Password must be at least 12 characters long'),
        ),
        confirm_password: zfd.text(z.string()),
        token: zfd.text(),
      }),
    ])
    .refine(
      (data) => {
        if (data.action === ACTIONS.UPDATE_PASSWORD) {
          return data.password === data.confirm_password
        }
        return true
      },
      {
        message: "Passwords don't match",
        path: ['confirm_password'],
      },
    ),
)

const SESSION_KEY = 'password_reset_error'

export default function PasswordReset() {
  const actionData = useActionData<typeof action>()
  const errors = actionData?.error?.fieldErrors
  const {hasStartedFlow, error} = useLoaderData<typeof loader>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const fetcher = useFetcher()

  if (token) {
    return (
      <Form
        method="POST"
        className="grid space-y-4 rounded-md bg-white/90 p-4 shadow-2xl backdrop-blur-sm"
      >
        <TextInput
          label="New Password"
          helperText="Must be at least 12 characters long."
          name="password"
          type="password"
          key="password"
          error={errors?.password}
        />
        <TextInput
          label="Confirm Password"
          name="confirm_password"
          type="password"
          key="confirm_password"
          error={errors?.confirm_password}
        />
        <input type="hidden" name="action" value={ACTIONS.UPDATE_PASSWORD} />
        <input type="hidden" name="token" value={token} />
        <Button>Reset password</Button>
      </Form>
    )
  }

  if (hasStartedFlow) {
    return (
      <div className="rounded-lg bg-white/90 p-4 p-4 shadow-2xl backdrop-blur-sm">
        <h1 className="font-title text-xl font-medium text-gray-600">
          Check your email.
        </h1>
        <p className="mt-2 font-body text-gray-600">
          We've just emailed you, follow the instructions in the email.
        </p>
        <fetcher.Form method="POST">
          <input type="hidden" name="action" value={ACTIONS.RESTART_FLOW} />
          <p className="mt-2 font-body text-gray-600">
            If after 10 minutes you still haven't received your email,{' '}
            <button type="submit" className="underline">
              click here
            </button>{' '}
            to start again.
          </p>
        </fetcher.Form>
        <p className="mt-2 font-body text-gray-600">
          Occasionally email filter systems may prevent you from receiving an
          email. If you do not receive an email, please contact us at{' '}
          <a href="mailto:sias@dragonflyintelligence.com" className="underline">
            sias@dragonflyintelligence.com
          </a>
        </p>
      </div>
    )
  }

  return (
    <Form method="POST" className="grid space-y-4">
      {error && <p className="mb-4 text-red-600">{error.message}</p>}

      <TextInput
        label="Email"
        name="email"
        key="email"
        error={actionData?.error?.fieldErrors?.email}
      />
      <input type="hidden" name="action" value={ACTIONS.START_FLOW} />
      <Button>Reset password</Button>
    </Form>
  )
}

export function ErrorBoundary({error}: {error: Error}) {
  console.error('error', error)

  return (
    <div className="rounded-lg p-4">
      <h1 className="font-title text-xl font-medium text-gray-600">
        Something went wrong.
      </h1>
      <p className="mt-2 font-body text-gray-600">
        Please try again later or contact us at{' '}
        <a href="mailto:sias@dragonflyintelligence.com" className="underline">
          sias@dragonflyintelligence.com
        </a>
      </p>
    </div>
  )
}
